import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ErrorBanner, GroupCard, ItemsSkeleton, SectionHeader } from '../components';
import { useSearch } from '../api/queries';
import { useDebounced } from '../hooks/useDebounced';
import { useSurface } from '../hooks/useSurface';

export function SearchScreen() {
  const navigate = useNavigate();
  const { isDesktop } = useSurface();
  const [query, setQuery] = useState('');
  const debounced = useDebounced(query);
  const { data: hits, isPending, isError, error, refetch } = useSearch(debounced);

  const hasQuery = query.trim().length > 0;
  // The skeleton is for the *first* result set only. Afterwards React Query
  // keeps the previous term's hits on screen (`placeholderData`) while the next
  // request is in flight, so typing does not strobe between list and skeleton.
  const isLoadingFirst = hasQuery && isPending;
  const isEmpty =
    hasQuery && !isLoadingFirst && !isError && debounced.trim().length > 0 && (hits?.length ?? 0) === 0;

  return (
    <>
      <div className="search-bar">
        <div className="search-bar__field">
          <span style={{ color: 'var(--tg-hint)', fontSize: 16 }} aria-hidden="true">
            ⌕
          </span>
          <input
            className="search-bar__input"
            value={query}
            autoFocus
            placeholder="Search all lists"
            aria-label="Search all lists"
            onChange={(e) => setQuery(e.target.value)}
          />
          <button
            type="button"
            className="search-bar__clear"
            aria-label="Clear search"
            onClick={() => (query ? setQuery('') : navigate('/'))}
          >
            ✕
          </button>
        </div>
      </div>

      {isError ? <ErrorBanner message={error.message} onRetry={() => void refetch()} /> : null}

      <div className={isDesktop ? 'screen-content content' : 'screen-content'}>
        {isLoadingFirst ? <ItemsSkeleton /> : null}

        {isEmpty ? (
          <div
            style={{
              minHeight: 280,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
              padding: 40,
            }}
          >
            <div style={{ fontSize: 40, color: 'var(--tg-hint)' }}>⌕</div>
            <div style={{ marginTop: 14, fontSize: 15, color: 'var(--tg-hint)' }}>
              No items match “{debounced.trim()}”.
            </div>
          </div>
        ) : null}

        {!hasQuery ? (
          <div
            style={{
              minHeight: 280,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'var(--tg-hint)',
              fontSize: 15,
              padding: 40,
              textAlign: 'center',
            }}
          >
            Type to search across every list you can see.
          </div>
        ) : null}

        {hasQuery && !isLoadingFirst && (hits?.length ?? 0) > 0 ? (
          <div style={{ padding: '6px 0 24px' }}>
            <SectionHeader>Recent items</SectionHeader>
            <GroupCard>
              {hits!.map((hit) => (
                <button
                  key={hit.itemId}
                  type="button"
                  className="search-row"
                  onClick={() => navigate(`/lists/${hit.listId}`)}
                >
                  <span className="search-row__check" aria-hidden="true">
                    {hit.done ? '✓' : ''}
                  </span>
                  <span style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
                    <span className={hit.done ? 'search-row__text is-done' : 'search-row__text'}>
                      {hit.text}
                    </span>
                    <span className="search-row__sub" style={{ display: 'block' }}>
                      in {hit.listName}
                      {hit.category ? ` · ${hit.category}` : ''}
                    </span>
                  </span>
                </button>
              ))}
            </GroupCard>
          </div>
        ) : null}
      </div>
    </>
  );
}
